'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Edit2, Trash2, Package } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { items, categories } from '../data/mock-data';
import type { ItemCategory } from '../types';
import { formatCurrency } from '../../../types';
import type { Item } from '../types';

// 품목 추가 모달
function ItemAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="품목 추가"
      description="새로운 품목을 등록합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="품목코드" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="품목명" required>
            <FormInput placeholder="품목명을 입력하세요" />
          </FormField>
          <FormField label="카테고리" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {categories.map((cat: ItemCategory) => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="단위" required>
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="EA">EA (개)</option>
              <option value="BOX">BOX (박스)</option>
              <option value="KG">KG (킬로그램)</option>
              <option value="M">M (미터)</option>
              <option value="ROLL">ROLL (롤)</option>
              <option value="SET">SET (세트)</option>
            </FormSelect>
          </FormField>
          <FormField label="판매가" required>
            <FormInput type="number" min={0} placeholder="0" />
          </FormField>
          <FormField label="원가">
            <FormInput type="number" min={0} placeholder="0" />
          </FormField>
          <FormField label="최소 재고">
            <FormInput type="number" min={0} placeholder="0" />
          </FormField>
          <FormField label="최대 재고">
            <FormInput type="number" min={0} placeholder="0" />
          </FormField>
          <FormField label="바코드">
            <FormInput placeholder="바코드를 입력하세요" />
          </FormField>
          <FormField label="상태">
            <FormSelect>
              <option value="active">활성</option>
              <option value="inactive">비활성</option>
            </FormSelect>
          </FormField>
        </div>
        <FormField label="품목 설명">
          <FormTextarea rows={3} placeholder="품목에 대한 설명을 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function ItemListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return items.filter((item) => {
      // 검색 필터
      const matchesSearch =
        item.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.barcode && item.barcode.includes(searchQuery));

      // 카테고리 필터
      const matchesCategory =
        selectedCategory === 'all' || item.category === selectedCategory;

      // 활성화 필터
      const matchesActive = showInactiveOnly ? !item.isActive : true;

      return matchesSearch && matchesCategory && matchesActive;
    });
  }, [searchQuery, selectedCategory, showInactiveOnly]);

  const columns = [
    {
      key: 'code',
      label: '품목코드',
      width: '8%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm">{value as string}</span>
      ),
    },
    {
      key: 'name',
      label: '품목명',
      width: '22%',
      sortable: true,
      render: (value: unknown, row: Item) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-accent flex items-center justify-center flex-shrink-0">
            <Package className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{value as string}</div>
            {row.description && (
              <div className="text-xs text-muted-foreground truncate">
                {row.description}
              </div>
            )}
          </div>
        </div>
      ),
    },
    { key: 'category', label: '카테고리', width: '8%', sortable: true },
    { key: 'unit', label: '단위', width: '5%', align: 'center' as const },
    {
      key: 'price',
      label: '판매가',
      width: '10%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'cost',
      label: '원가',
      width: '10%',
      align: 'right' as const,
      render: (value: unknown) => (
        <span className="text-muted-foreground whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'minStock',
      label: '최소',
      width: '6%',
      align: 'right' as const,
    },
    {
      key: 'maxStock',
      label: '최대',
      width: '6%',
      align: 'right' as const,
    },
    {
      key: 'isActive',
      label: '상태',
      width: '7%',
      render: (value: unknown) => (
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
            value
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
          }`}
        >
          {value ? '활성' : '비활성'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '6%',
      render: (_: unknown, row: Item) => (
        <div className="flex items-center gap-1">
          <button
            className="p-1.5 rounded hover:bg-accent"
            title="수정"
          >
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
          <button
            className="p-1.5 rounded hover:bg-accent"
            title="삭제"
          >
            <Trash2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="품목 관리"
        description="판매 및 재고 품목을 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            품목 추가
          </button>
        }
      />

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="품목코드, 품목명, 바코드 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 카테고리 필터 */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 카테고리</option>
          {categories.map((cat: ItemCategory) => (
            <option key={cat.id} value={cat.name}>
              {cat.name} ({cat.itemCount})
            </option>
          ))}
        </select>

        {/* 비활성 품목만 보기 */}
        <label className="flex items-center gap-2 h-10 px-3 rounded-lg border border-border bg-background text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showInactiveOnly}
            onChange={(e) => setShowInactiveOnly(e.target.checked)}
            className="w-4 h-4 rounded border-border"
          />
          <span>비활성 품목만</span>
        </label>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 품목 추가 모달 */}
      <ItemAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
